import { useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { mediaFilename, useMediaBlob } from './useMediaBlob';
import { EngagementBar } from '../engagement/EngagementBar';
import { useEngagement } from '../../data/queries';
import { useT } from '../../i18n';
import type { MediaRow, EngagementEntityType } from '../../data/dto';
import { Icon } from '../chrome/Icon';

interface Props {
  items: MediaRow[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  canDecrypt: boolean;
  decrypt: (bytes: Uint8Array) => Uint8Array;
  /** When set, enables comments/reactions on each (baby-owned) photo. */
  babyId?: string | null;
  onOpenMedia?: () => void;
  entityType?: EngagementEntityType;
  entityId?: string | null;
  /** When set, shows a delete button (left of the comment icon) that removes
   *  the current photo. Presence of this prop is the permission gate —
   *  callers only pass it when the viewer may delete (owner/editor). */
  onDelete?: (media: MediaRow) => Promise<void> | void;
}

/**
 * Fullscreen photo viewer: large object-contained image, prev/next (arrows,
 * keyboard, and touch swipe), plus download + share.
 */
export function MediaLightbox({ items, index, onIndex, onClose, canDecrypt, decrypt, babyId, onOpenMedia, entityType, entityId, onDelete }: Props) {
  const t = useT();
  const media = items[index];
  const { state, url, blob } = useMediaBlob(media, canDecrypt, decrypt);
  const [showEngage, setShowEngage] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const targetType = entityType || 'media';
  const targetId = entityType && entityId ? entityId : (media?.id ?? null);
  const canEngage = !!babyId && !!targetId;

  // Auto-open the comments/reactions panel when the enlarged photo already has
  // engagement. Shares the cache the thumbnail prefilled, so this is usually
  // resolved instantly. We decide once per photo, so a manual toggle sticks.
  const eng = useEngagement(babyId ?? null, targetType, targetId);
  const decidedFor = useRef<string | null>(null);
  useEffect(() => {
    const id = targetId;
    if (!canEngage || !id) return;
    if (decidedFor.current === id) return;
    if (eng.data === undefined) return; // wait for the query to resolve
    decidedFor.current = id;
    setShowEngage(eng.data.comments.length > 0 || eng.data.reactions.length > 0);
  }, [canEngage, targetId, eng.data]);

  const canPrev = index > 0;
  const canNext = index < items.length - 1;
  const go = (delta: number) => {
    const next = index + delta;
    if (next >= 0 && next < items.length) onIndex(next);
  };

  // Keyboard nav + lock background scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, items.length]);

  // Touch swipe.
  const touchX = useRef<number | null>(null);
  const onTouchStart = (e: ReactTouchEvent) => { touchX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = mediaFilename(media);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const nav = navigator as Navigator & { canShare?: (d?: ShareData) => boolean };
  const canShareFiles = typeof nav.canShare === 'function' && typeof nav.share === 'function';
  const share = async () => {
    if (!blob) return;
    const file = new File([blob], mediaFilename(media), { type: media.mime_type });
    try {
      if (nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: 'BabyNook photo' });
        return;
      }
    } catch {
      /* user cancelled or share failed → fall back */
    }
    download();
  };

  const handleDelete = async () => {
    if (!onDelete || !media || deleting) return;
    const confirmed = window.confirm(t('media.deleteConfirm', "Delete this photo? This can't be undone."));
    if (!confirmed) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(media);
      if (items.length <= 1) {
        onClose();
      } else if (index === items.length - 1) {
        onIndex(index - 1);
      }
      // else: stay at the same index — once the caller's list refetches and
      // shrinks, the next photo slides into this position.
    } catch {
      setDeleteError(t('media.deleteFailed', "Couldn't delete this photo."));
    } finally {
      setDeleting(false);
    }
  };

  // Rendered into <body> so it sits in the root stacking context and can never
  // be trapped under the sidebar / tab bar (which live in their own contexts).
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/92 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 pt-[calc(env(safe-area-inset-top)+12px)] pb-3 text-white/90">
        <button onClick={onClose} aria-label="Close" className="flex size-10 items-center justify-center rounded-full bg-white/10">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        <span className="text-[13px] font-semibold tabular-nums">{index + 1} / {items.length}</span>
        <div className="flex items-center gap-2">
          {onOpenMedia && (
            <button
              onClick={() => {
                onClose();
                onOpenMedia();
              }}
              aria-label="View Media Gallery"
              className="flex size-10 items-center justify-center rounded-full bg-white/10"
            >
              <Icon name="photos" size={19} color="white" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              aria-label={t('media.delete', 'Delete photo')}
              className="flex size-10 items-center justify-center rounded-full bg-white/10 disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-8 0v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V7" />
                <path d="M10 11v6M14 11v6" />
              </svg>
            </button>
          )}
          {canEngage && (
            <button
              onClick={() => setShowEngage((v) => !v)}
              aria-label={t('engagement.title', 'Comments & reactions')}
              aria-pressed={showEngage}
              className={[
                'flex size-10 items-center justify-center rounded-full',
                showEngage ? 'bg-white/25' : 'bg-white/10',
              ].join(' ')}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6a8.5 8.5 0 0 1-.9-3.9A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
              </svg>
            </button>
          )}
          {canShareFiles && (
            <button onClick={share} aria-label="Share" className="flex size-10 items-center justify-center rounded-full bg-white/10">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
                <path d="M12 3v13M8 7l4-4 4 4" />
              </svg>
            </button>
          )}
          <button onClick={download} aria-label="Download" className="flex size-10 items-center justify-center rounded-full bg-white/10">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v12M8 11l4 4 4-4" />
              <path d="M5 21h14" />
            </svg>
          </button>
        </div>
      </div>

      {deleteError && (
        <p className="px-4 pb-2 text-center text-[12.5px] font-medium text-red-300">{deleteError}</p>
      )}

      {/* Image stage */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden px-2">
        {state === 'ready' && url ? (
          media.media_type === 'video' ? (
            <video controls src={url} className="max-h-full max-w-full object-contain" />
          ) : (
            <img src={url} alt="" className="max-h-full max-w-full object-contain select-none" draggable={false} />
          )
        ) : state === 'error' ? (
          <p className="text-white/70 text-sm">{t('media.loadFailed')}</p>
        ) : (
          <span className="size-7 animate-pulse rounded-full bg-white/20" />
        )}

        {canPrev && (
          <button onClick={() => go(-1)} aria-label="Previous" className="absolute left-2 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white/90">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
          </button>
        )}
        {canNext && (
          <button onClick={() => go(1)} aria-label="Next" className="absolute right-2 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white/90">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        )}
      </div>

      {/* Engagement sheet — light panel anchored to the bottom. */}
      {canEngage && showEngage && babyId && targetId && (
        <div className="max-h-[55%] overflow-y-auto rounded-t-2xl bg-bg px-5 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4">
          <EngagementBar babyId={babyId} entityType={targetType} entityId={targetId} />
        </div>
      )}

      <div className="h-[env(safe-area-inset-bottom)]" />
    </div>,
    document.body,
  );
}
